// frontend/app/components/lessonPlans/teachsense/TeachSenseWrittenAnswers.js
//
// The written answers, somewhere a teacher can actually read them.
//
// Class insights has always been able to say "3 written answers to read" —
// they carry no right/wrong, so they never reach a mastery bar, and the count
// was the only trace of them anywhere in the product. The count was built; the
// destination was not, and the heaviest thinking in the lesson was visible only
// as a number. This is the destination.
//
// Four things here are the reason the section exists rather than decoration on
// top of it.
//
// The prompt is drawn above every answer, on every row, even when the row above
// it repeats the same prompt. An explanation is unreadable without the question
// it answers, and a teacher mid-lesson is scanning a class rather than sitting
// with one student.
//
// The AI mark sits beside the words and is labelled with whose mark it is. It
// is never drawn instead of the text and the text is never collapsed behind it:
// the whole point of putting a teacher in front of these is that a human can
// disagree with the model.
//
// An answer with no mark is drawn at full weight with a "Not marked yet" pill,
// never hidden and never sorted to the bottom. Unmarked is the state that most
// needs a person, so it must be the easiest state to find.
//
// And the order is the server's — newest first — kept rather than regrouped by
// prompt. A teacher opening this while the class is working wants what just
// came in; grouping would bury it under the first answer of the lesson.

import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { Pill } from "../blocks/LessonBlockShell";
import { getSectionForPhase, getSectionMeta, tint } from "../lessonPlanTheme";
import { bandForScore, bandVisual, masteryPercent } from "./masteryBands";
import { MAX_ROWS, Section, StudentRow } from "./TeachSenseSection";
import {
  feedbackSource,
  feedbackText,
  fill,
  humanizeKey,
  relativeTime,
} from "./teachsenseText";

// Neutral slate for a row whose phase the theme has never heard of; the same
// hex the unanswered band uses. A hex rather than a token because tint() blends
// a wash from one.
const NEUTRAL_HEX = "#64748b";

const trimmed = (value) => String(value ?? "").trim();

/** Answers filed under one concept, in the order they arrived. */
export const filterByConcept = (entries, conceptKey) => {
  const key = trimmed(conceptKey);
  if (!key) return entries;
  return entries.filter((entry) => trimmed(entry?.concept_key) === key);
};

/**
 * A [0,1] AI score as a percentage plus the band it falls in, or null when the
 * answer has not been marked. `null` and `0` are different findings — nobody
 * has read it versus the model read it and found nothing — so an unparseable
 * score never becomes a zero.
 */
export const scoreOf = (entry, colors, t) => {
  const raw = entry?.ai_score;
  const pct = masteryPercent(raw);
  if (pct === null) return null;
  return { pct, band: bandVisual(bandForScore(raw), colors, t) };
};

const AnswerRow = ({ entry, colors, isDarkMode, divided, t }) => {
  const text = trimmed(entry?.text);
  const prompt = trimmed(entry?.prompt);
  const phaseKey = trimmed(entry?.phase).toLowerCase();
  // Teachers see the four sections, never the phase an answer came from.
  const sectionKey = phaseKey ? getSectionForPhase(phaseKey) : null;
  const phase = sectionKey ? getSectionMeta(sectionKey) : null;
  const score = scoreOf(entry, colors, t);
  const note = feedbackText(entry?.ai_feedback);
  const source = feedbackSource(entry?.ai_feedback);
  const since = relativeTime(entry?.answered_at, t);

  const blockLabel = humanizeKey(entry?.block_type);
  const concept = trimmed(entry?.concept_key);
  const meta = [phase ? t(phase.label) : "", blockLabel, concept, since]
    .filter(Boolean)
    .join(" · ");

  // The prompt is what makes the answer legible, so an unauthored one says so
  // rather than leaving the words floating under a name. The block and step it
  // came from are the only identification left.
  const promptLine =
    prompt ||
    fill(t("Untitled {block} in {step}"), {
      block: (blockLabel || t("prompt")).toLowerCase(),
      step: trimmed(entry?.step_key) || t("this lesson"),
    });

  return (
    <StudentRow
      entry={entry}
      colors={colors}
      isDarkMode={isDarkMode}
      accent={phase ? phase.accent : colors.textSecondary}
      hex={phase ? phase.accent : NEUTRAL_HEX}
      divided={divided}
      // The screen-reader line reads the question before the answer for the
      // same reason the printed row does.
      detail={`${promptLine}. ${text}`}
      trailing={
        score ? (
          <Pill
            colors={colors}
            // Labelled with whose mark it is: a teacher must never mistake the
            // model's reading of an answer for their own.
            label={fill(t("Saras {n}%"), { n: score.pct })}
            accent={score.band.accent}
            background={tint(score.band.hex, isDarkMode, 1.4)}
          />
        ) : (
          <Pill
            colors={colors}
            label={t("Not marked yet")}
            accent={colors.textSecondary}
            background={colors.subtleBg}
          />
        )
      }
    >
      <Text style={[styles.prompt, { color: prompt ? colors.textPrimary : colors.textMuted }]}>
        {promptLine}
      </Text>
      <View
        style={[styles.answer, { backgroundColor: colors.subtleBg, borderColor: colors.border }]}
      >
        <Text style={[styles.answerText, { color: colors.textPrimary }]}>{text}</Text>
      </View>
      {note ? (
        <View
          style={[styles.note, { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder }]}
        >
          <Text style={[styles.noteSource, { color: colors.accentPrimary }]}>
            {source === "heuristic" ? t("Automatic check") : t("Saras AI")}
          </Text>
          <Text style={[styles.noteText, { color: colors.textSecondary }]}>{note}</Text>
        </View>
      ) : null}
      {meta ? <Text style={[styles.meta, { color: colors.textMuted }]}>{meta}</Text> : null}
    </StudentRow>
  );
};

const TeachSenseWrittenAnswers = ({
  writtenAnswers = null,
  // Set when a teacher arrived here by pressing "N written answers to read" on
  // a concept in class insights. The section shows that concept only, and says
  // so, because silently rendering the whole class would look like the press
  // did nothing.
  focusConcept = null,
  focusLabel = "",
  onClearFocus = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  // Every other section caps at MAX_ROWS and leaves the rest as a count. This
  // one is the place a teacher was sent to read work, so the rest has to be
  // reachable — the cap is a first screenful, not a ceiling.
  const [expanded, setExpanded] = useState(false);

  // A blank answer is a block a student opened and left; the server already
  // drops those, and this repeats it so a stray row cannot draw an empty
  // quote box.
  const entries = (Array.isArray(writtenAnswers) ? writtenAnswers : []).filter((entry) =>
    trimmed(entry?.text)
  );

  const focusKey = trimmed(focusConcept);
  const filtered = filterByConcept(entries, focusKey);
  const shown = expanded ? filtered : filtered.slice(0, MAX_ROWS);
  const overflow = filtered.length - shown.length;
  const unmarked = filtered.filter((entry) => scoreOf(entry, colors, translate) === null).length;

  // The count beside "N written answers to read" is every ungraded response on
  // the concept, which includes predictions and reflections — those are read in
  // their own sections and are deliberately not repeated here. So a focus can
  // legitimately match nothing, and that has to read as an answer rather than
  // as the press having failed.
  const focusedEmpty = Boolean(focusKey) && entries.length > 0 && filtered.length === 0;

  const subtitle = unmarked
    ? fill(
        translate("Newest first. {n} of {total} have no AI mark yet — those are yours to judge."),
        { n: unmarked, total: filtered.length }
      )
    : translate("Newest first. The AI mark is a second opinion, never a substitute for reading.");

  return (
    <Section
      testID="teachsense-written-answers"
      colors={colors}
      isNarrow={isNarrow}
      icon="text-box-search-outline"
      accent={colors.accentPrimary}
      title={translate("Written answers")}
      subtitle={filtered.length ? subtitle : ""}
      count={filtered.length || null}
      isEmpty={entries.length === 0}
      empty={translate("Nothing has been written in this lesson yet.")}
    >
      {focusKey ? (
        <FocusBanner
          label={focusLabel || focusKey}
          onClear={onClearFocus}
          colors={colors}
          t={translate}
        />
      ) : null}

      {focusedEmpty ? (
        <Text style={[styles.empty, { color: colors.textMuted }]}>
          {fill(
            translate(
              "No explanations or short answers are filed under {concept}. Predictions and reflections are read in their own sections below."
            ),
            { concept: focusLabel || focusKey }
          )}
        </Text>
      ) : (
        shown.map((entry, index) => (
          <AnswerRow
            key={`${entry.student_email || "anon"}-${entry.step_key || ""}-${
              entry.block_id || ""
            }-${index}`}
            entry={entry}
            colors={colors}
            isDarkMode={isDarkMode}
            divided={index > 0 || Boolean(focusKey)}
            t={translate}
          />
        ))
      )}

      {overflow > 0 ? (
        <TouchableOpacity
          testID="teachsense-written-more"
          accessibilityRole="button"
          accessibilityLabel={fill(translate("Show {n} more answers"), { n: overflow })}
          onPress={() => setExpanded(true)}
          style={styles.moreButton}
        >
          <Text style={[styles.more, { color: colors.info }]}>
            {fill(translate("Show {n} more answers"), { n: overflow })}
          </Text>
          <MaterialCommunityIcons name="chevron-down" size={14} color={colors.info} />
        </TouchableOpacity>
      ) : null}
    </Section>
  );
};

/**
 * Says which concept is being shown and offers the way out of it. Pressable
 * only when the caller can actually clear the filter — a dead control that
 * looks live is the failure this whole section is fixing.
 */
const FocusBanner = ({ label, onClear, colors, t }) => {
  const body = (
    <>
      <MaterialCommunityIcons name="filter-variant" size={13} color={colors.info} />
      <Text style={[styles.focusText, { color: colors.textSecondary }]} numberOfLines={1}>
        {fill(t("Showing {concept} only"), { concept: label })}
      </Text>
      {onClear ? (
        <Text style={[styles.focusClear, { color: colors.info }]}>{t("Show all")}</Text>
      ) : null}
    </>
  );

  const style = [styles.focus, { backgroundColor: colors.subtleBg, borderColor: colors.border }];

  return onClear ? (
    <TouchableOpacity
      testID="teachsense-written-focus"
      accessibilityRole="button"
      accessibilityLabel={t("Show every written answer")}
      onPress={onClear}
      style={style}
    >
      {body}
    </TouchableOpacity>
  ) : (
    <View testID="teachsense-written-focus" style={style}>
      {body}
    </View>
  );
};

const styles = StyleSheet.create({
  focus: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 5,
    marginBottom: 4,
  },
  focusText: { fontSize: 12, fontWeight: "600", lineHeight: 16, flexShrink: 1 },
  focusClear: { fontSize: 12, fontWeight: "800", lineHeight: 16 },

  prompt: { fontSize: 13, fontWeight: "700", lineHeight: 18, marginTop: 2, maxWidth: 720 },
  answer: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 6,
    maxWidth: 720,
  },
  // The student's own words are the largest type in the row. Everything else
  // on it is context for these two lines.
  answerText: { fontSize: 14, fontWeight: "500", lineHeight: 20 },

  note: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 8,
    maxWidth: 720,
  },
  noteSource: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  noteText: { fontSize: 12, lineHeight: 18 },

  meta: { fontSize: 11, fontWeight: "500", lineHeight: 15, marginTop: 6 },
  empty: { fontSize: 13, lineHeight: 19, fontStyle: "italic", marginTop: 8, maxWidth: 720 },

  moreButton: { flexDirection: "row", alignItems: "center", alignSelf: "flex-start", gap: 4, paddingTop: 10 },
  more: { fontSize: 12, fontWeight: "700" },
});

export default TeachSenseWrittenAnswers;
